import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { apiService } from '../services/api';
import { UserRole } from '../types/api';

// Greek translations
const translations = {
  en: {
    // Common
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    save: 'Save',
    cancel: 'Cancel',
    edit: 'Edit',
    delete: 'Delete',
    add: 'Add',
    create: 'Create',
    update: 'Update',
    search: 'Search',
    confirm: 'Confirm',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    close: 'Close',
    submit: 'Submit',
    reset: 'Reset',
    clear: 'Clear',
    required: 'Required',
    optional: 'Optional',
    yes: 'Yes',
    no: 'No',
    
    // Edit Subscription Modal
    subscriptionUpdatedSuccessfully: 'Subscription updated successfully',
    failedToUpdateSubscription: 'Failed to update subscription',
    errorUpdatingSubscription: 'Error updating subscription',
    subscriptionFrozenSuccessfully: 'Subscription frozen successfully',
    failedToFreezeSubscription: 'Failed to freeze subscription',
    errorFreezingSubscription: 'Error freezing subscription',
    subscriptionUnfrozenSuccessfully: 'Subscription unfrozen successfully',
    failedToUnfreezeSubscription: 'Failed to unfreeze subscription',
    errorUnfreezingSubscription: 'Error unfreezing subscription',
    currentSubscription: 'Current Subscription',
    unfreeze: 'Unfreeze',
    freeze: 'Freeze',
    unfreezeSubscription: 'Unfreeze this subscription',
    freezeSubscription: 'Freeze this subscription',
    appointmentsPerMonth: 'appointments per month',
    clientHasNoSubscription: 'This client does not currently have a subscription.',
    updateSubscription: 'Update Subscription',
    createNewSubscription: 'Create New Subscription',
    updateCurrentSubscription: 'Update Current Subscription',
    createNewSubscriptionDescription: 'Create a completely new subscription for this client (will replace existing subscription)',
    updateSubscriptionDescription: 'Modify the current subscription details including actual dates',
    subscriptionCreatedSuccessfully: 'Subscription created successfully',
    failedToCreateSubscription: 'Failed to create subscription',
    errorCreatingSubscription: 'Error creating subscription',
    creating: 'Creating...',
    activeSubscription: 'Active Subscription',
    createSubscription: 'Create Subscription',
    
    // Edit Appointments Modal
    manageAppointments: 'Manage Appointments',
    failedToLoadClientAppointments: 'Failed to load client appointments',
    failedToLoadAvailableTimeslots: 'Failed to load available timeslots',
    confirmBookAppointmentMessage: 'Do you want to book an appointment for',
    failedToBookAppointment: 'Failed to book appointment',
    errorBookingAppointment: 'Error booking appointment',
    cancelAppointment: 'Cancel Appointment',
    confirmCancelAppointmentMessage: 'Are you sure you want to cancel this appointment? This action cannot be undone.',
    cancelCountsTowardLimit: 'Count this cancellation toward the client\'s monthly appointment limit',
    cancellationDeadlineWarning: 'The free cancellation deadline has passed. If you proceed, your spot will be released and the session will be charged normally to your package. Do you wish to continue?',
    failedToCancelAppointment: 'Failed to cancel appointment',
    errorCancellingAppointment: 'Error cancelling appointment',
    deleteAppointment: 'Delete Appointment',
    confirmPermanentDeleteMessage: 'Are you sure you want to permanently delete this appointment? This action cannot be undone.',
    appointmentDeletedSuccessfully: 'Appointment deleted successfully',
    failedToDeleteAppointment: 'Failed to delete appointment',
    errorDeletingAppointment: 'Error deleting appointment',
    loadingAvailableTimeSlots: 'Loading available time slots...',
    noAvailableTimeSlots: 'No Available Time Slots',
    noAvailableTimeSlotsMessage: 'There are no available time slots for this date. Please select a different date.',
    noAppointmentsFound: 'No appointments found.',
    at: 'at',
    by: 'by',
    of: 'of',
    
    // Edit Client Info Modal
    clientInfoUpdatedSuccessfully: 'Client information updated successfully',
    failedToUpdateClient: 'Failed to update client',
    errorUpdatingClient: 'Error updating client',
    receiveMailNotifications: 'Receive mail notifications for client',
    updateClientInfo: 'Update Client Info',
    
    // Pagination
    itemsPerPage: 'Items per page',
    showingItemsRange: 'Showing {start}-{end} of {total} items',
    
    // Navigation & Layout
    welcome: 'Welcome',
    dashboard: 'Dashboard',
    logout: 'Logout',
    administrator: 'Administrator',
    client: 'Trainee',
    
    // Auth
    login: 'Login',
    signIn: 'Sign In',
    signingIn: 'Signing in...',
    pleaseSignInToContinue: 'Please sign in to continue',
    email: 'Email',
    password: 'Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm Password',
    confirmNewPassword: 'Confirm New Password',
    fullName: 'Full Name',
    phoneNumber: 'Phone Number',
    createAccount: 'Create Account',
    creatingAccount: 'Creating Account...',
    joinReforged: 'Join Reforged Training Zone',
    backToLogin: 'Back to Login',
    forgotPassword: 'Forgot Password?',
    resetPassword: 'Reset Password',
    sendResetInstructions: 'Send Reset Instructions',
    sending: 'Sending...',
    enterEmailToReceiveReset: 'Enter your email to receive reset instructions',
    emailAddress: 'Email Address',
    securityVerification: 'Security Verification',
    
    // Dashboard Navigation
    overview: 'Overview',
    clientManagement: 'Client Management',
    settings: 'Settings',
    appointment: 'Appointment',
    bookAppointment: 'Book Appointment',
    myProfile: 'My Profile',
    
    // Admin Overview
    upcomingAppointments: 'Upcoming Appointments',
    activeSubscriptions: 'Active Subscriptions',
    inactiveClients: 'Inactive Clients',
    totalClients: 'Total Clients',
    statistics: 'Statistics',
    clientTotals: 'Client Totals',
    clientDemographics: 'Client Demographics',
    maleClients: 'Male Clients',
    femaleClients: 'Female Clients',
    genderNotSpecified: 'Not Specified',
    morningClients: 'Morning Clients',
    afternoonClients: 'Afternoon Clients',
    regularClients: 'Regular Clients',
    noUpcomingAppointments: 'No upcoming appointments for',
    loadingAppointments: 'Loading appointments...',
    searchClientsByName: 'Search clients by name...',
    noActiveSubscriptions: 'No active subscriptions found',
    noClientsFound: 'No clients found matching',
    
    // Client Management
    addNewClient: 'Add New Client',
    searchClients: 'Search clients...',
    noClientsFoundGeneral: 'No clients found.',
    name: 'Name',
    subscriptionExpires: 'Subscription Expires',
    actions: 'Actions',
    noSubscription: 'No subscription',
    info: 'Info',
    subscription: 'Subscription',
    appointments: 'Appointments',
    
    // Appointments
    totalAppointments: 'Completed Appointments',
    upcoming: 'Upcoming',
    thisMonth: 'This Month',
    appointmentHistory: 'Appointment History',
    noUpcomingAppointmentsShort: 'No upcoming appointments.',
    bookNewAppointment: 'Book New Appointment',
    noAppointmentHistory: 'No appointment history available.',
    showingLastAppointments: 'Showing last 5 appointments',
    time: 'Time',
    booked: 'Booked',
    cancelledAt: 'Cancelled At',
    date: 'Date',
    status: 'Status',
    
    // Timeslots & Booking
    availableTimeSlots: 'Available Time Slots',
    noTimeSlots: 'No Time Slots',
    noTimeSlotsMessage: 'There are no time slots for this date. Please select a different date.',
    bookThisSlot: 'Book This Slot',
    booking: 'Booking...',
    expressInterest: 'Express Interest',
    expressing: 'Expressing...',
    interestExpressed: 'Interest Expressed',
    notificationWhenSpotAvailable: 'You will be notified if a spot becomes available.',
    alreadyExpressedInterest: 'You have already expressed interest in this timeslot.',
    removeInterest: 'Remove Interest',
    removing: 'Removing...',
    full: 'Full',
    available: 'Available',
    capacity: 'Capacity',
    loadingTimeslots: 'Loading timeslots...',
    
    // Subscription
    subscriptionPeriod: 'Subscription Period',
    monthlyLimit: 'Monthly Limit',
    usedThisMonth: 'Charged Sessions',
    appointmentDates: 'Appointment Dates',
    dateOutsideSubscription: 'Date Outside Subscription Period',
    cannotBookOutsideSubscription: 'You cannot book appointments for this date because this date is outside your subscription period.',
    subscriptionValidFrom: 'Your subscription is valid from',
    to: 'to',
    selectDateWithinSubscription: 'Please select a date within your subscription period or contact the gym for assistance.',

    // Subscription History
    subscriptionHistory: 'Subscription History',
    viewHistory: 'View History',
    loadingSubscriptionHistory: 'Loading subscription history...',
    failedToLoadSubscriptionHistory: 'Failed to load subscription history',
    errorLoadingSubscriptionHistory: 'Error loading subscription history',
    noSubscriptionHistory: 'No subscription history found.',
    actualPeriod: 'Actual Period',
    createdAt: 'Created At',

    // Settings
    adminSettings: 'Admin Settings',
    manageGymConfiguration: 'Manage gym-wide configuration and timeslots',
    globalSettings: 'Global Settings',
    globalTimeslots: 'Global Timeslots',
    broadcastEmail: 'Broadcast Email',
    loadingSettings: 'Loading settings...',
    currentValue: 'Current Value',
    lastUpdated: 'Last updated',
    
    // Status badges
    confirmed: 'Confirmed',
    cancelled: 'Cancelled',
    completed: 'Completed',
    pending: 'Pending',
    
    // Errors & Messages
    failedToLoadData: 'Failed to load data. Please try again.',
    failedToLoadClients: 'Failed to load clients',
    failedToLoadSettings: 'Failed to load settings',
    registrationSuccessful: 'Registration successful. Please check your email to confirm your account.',
    emailConfirmationRequired: 'Login failed. Please check your credentials or contact support if you need assistance.',
    loginFailedInvalidCredentials: 'Invalid email or password. Please check your credentials and try again.',
    loginFailedEmailNotConfirmed: 'Your email address has not been confirmed. Please check your email for a confirmation link.',
    loginFailedUserNotActive: 'Your account has been deactivated. Please contact the gym administrator for assistance.',
    loginFailedNoActiveSubscription: 'No active subscription found. Please contact the administrator to activate your membership.',
    loginFailedGeneric: 'An error occurred during login. Please try again.',
    passwordResetSent: 'If the email address exists in our system, you will receive password reset instructions.',
    
    // Booking Info
    bookingInformation: 'Booking Information',
    bookingRules: [
      'You can book appointments up to 30 days in advance',
      'Appointments can be cancelled up to 2 hours before the scheduled time',
      'Please arrive 5-10 minutes before your scheduled time',
      'You cannot book the same appointment time slot on the same day',
      'For full time slots, you can express interest to be notified when a spot becomes available',
      'You can manage your expressed interests and remove them at any time'
    ],
    morningClientRestriction: 'Morning Client Restriction: As a morning client, you can only book time slots before 3:00 PM',
    afternoonClientRestriction: 'Afternoon Client Restriction: As an afternoon client, you can only book time slots at or after 3:00 PM',
    
    // Reset Password
    resetPasswordTitle: 'Reset Password',
    enterYourNewPassword: 'Enter your new password',
    forEmail: 'for',
    newPasswordRequired: 'New password is required',
    passwordMinLength: 'Password must be at least 6 characters long',
    passwordMismatch: 'Password and confirmation password do not match',
    enterNewPasswordPlaceholder: 'Enter your new password (min 6 characters)',
    confirmNewPasswordPlaceholder: 'Confirm your new password',
    resettingPassword: 'Resetting Password...',
    passwordResetSuccessfully: 'Password reset successfully. You can now log in with your new password.',
    redirectingToLogin: 'Redirecting to login...',
    passwordResetFailed: 'Password reset failed',
    errorResettingPassword: 'An error occurred while resetting your password',
    invalidResetLink: 'Invalid reset link. Please request a new password reset.',
    
    // Email Confirmation
    emailConfirmation: 'Email Confirmation',
    confirmingYourEmail: 'Confirming your email...',
    pleaseWaitWhileWeVerify: 'Please wait while we verify your email address.',
    emailConfirmedSuccessfully: 'Email Confirmed Successfully!',
    yourEmailHasBeenConfirmed: 'Your email has been successfully confirmed! You can now log in to your account.',
    youWillBeRedirected: 'You will be redirected to the login page in',
    seconds: 'seconds',
    goToLoginNow: 'Go to Login Now',
    confirmationFailed: 'Confirmation Failed',
    returnToLogin: 'Return to Login',
    requestNewLink: 'Request New Link',
    needHelp: 'Need help? Contact our support team.',
    invalidConfirmationLink: 'Invalid confirmation link. Please check your email and try again.',
    emailConfirmationFailed: 'Email confirmation failed. Please try again or contact support.',
    invalidOrExpiredLink: 'Invalid or expired confirmation link. Please request a new confirmation email.',
    errorDuringConfirmation: 'An error occurred during email confirmation. Please try again or contact support.',
    emailConfirmedLoginMessage: 'Email confirmed successfully! Please log in.',
    emailAlreadyConfirmed: 'Your email has already been confirmed. You can now log in to your account.',
    checkEmailForNewLink: 'Please check your email for a new confirmation link.',
    resendVerificationEmail: 'Resend Verification Email',
    verificationEmailResent: 'Verification email has been sent to your inbox. Please check your email.',
    failedToResendEmail: 'Failed to resend verification email. Please try again.',
    noAccountFoundWithEmail: 'No account found with this email address.',
    emailAlreadyVerifiedCanLogin: 'Email already verified. You can log in.',
    accountDisabledContactSupport: 'Account disabled. Contact support.',
    securityCheckFailedTryAgain: 'Security check failed. Please try again.',
    invalidInputValidation: 'Invalid input. Please check your details.',
    somethingWentWrongTryAgainLater: 'Something went wrong. Please try again later.',
    rateLimitExceeded: 'Please wait before requesting another email.',
    pleaseCompleteCaptcha: 'Please complete the security verification below:',
    
    // Settings & Admin
    gymCapacityPerTimeslot: 'Gym Capacity Per Timeslot',
    regularClientCancellationDeadline: 'Regular Client Cancellation Deadline (Hours)',
    morningClientCancellationDeadline: 'Morning Client Cancellation Deadline (Hours)',
    maxClientsPerTimeslot: 'Maximum number of clients that can book each timeslot',
    cancellationDeadlineRegular: 'Hours before appointment when cancellation still counts for regular clients',
    cancellationDeadlineMorning: 'Hours before appointment when cancellation still counts for morning clients',
    morningAppointmentCutoff: 'Morning Appointment Cutoff',
    morningAppointmentCutoffTime: 'Time that defines when an appointment is considered a morning appointment',
    morningClientBookingCutoff: 'Morning Client Booking Cutoff',
    morningClientBookingCutoffDescription: 'Morning clients can only book timeslots that start before this time',
    afternoonClientBookingStart: 'Afternoon Client Booking Start',
    afternoonClientBookingStartDescription: 'Afternoon clients can only book timeslots that start at or after this time',
    failedToUpdateSetting: 'Failed to update setting',
    globalSetting: 'Global setting',
    saving: 'Saving...',
    
    // Timeslot Management
    timeslotManagement: 'Timeslot Management',
    selectDate: 'Select Date',
    previousDay: 'Previous Day',
    nextDay: 'Next Day',
    previousMonth: 'Previous Month',
    nextMonth: 'Next Month',
    timeslots: 'Timeslots',
    noTimeslotsForDate: 'No timeslots available for this date',
    failedToLoadTimeslots: 'Failed to load timeslots',
    bookClient: 'Book Client',
    selectClient: 'Select Client',
    selectClientPlaceholder: 'Choose a client...',
    reason: 'Reason',
    viewClients: 'View Clients',
    changeTime: 'Change Time',
    disableTimeslot: 'Disable Timeslot',
    closeDay: 'Close Day',
    closeDateRange: 'Close Date Range',
    bulkCloseTimeslots: 'Bulk Close Timeslots',
    newStartTime: 'New Start Time',
    newEndTime: 'New End Time',
    reasonForChange: 'Reason for change',
    reasonForClosure: 'Reason for closure',
    reasonForClosing: 'Reason for closing',
    closeDayPlaceholder: 'e.g., Public holiday, Maintenance, etc.',
    closeDateRangePlaceholder: 'e.g., Holiday break, Renovations, etc.',
    selectTimeslotsToClose: 'Select timeslots to close',
    bulkCloseReasonPlaceholder: 'e.g., Maintenance break, Staff training, etc.',
    reviewAndClose: 'Review and Close',
    confirmBulkClose: 'Confirm Bulk Close',
    bulkCloseConfirmationMessage: 'You are about to close the selected timeslots for the specified date range. Please review the details below:',
    dateRange: 'Date Range',
    selectedTimeslots: 'Selected Timeslots',
    totalOperations: 'Total Operations',
    confirmAndClose: 'Confirm and Close',
    operationResults: 'Operation Results',
    successful: 'Successful',
    failed: 'Failed',
    errors: 'Errors',
    pleaseSelectAtLeastOneTimeslot: 'Please select at least one timeslot',
    bulkOperationCompletedSuccessfully: 'Bulk operation completed successfully',
    bulkOperationPartialSuccess: 'Bulk operation partially successful',
    bulkOperationFailed: 'Bulk operation failed',
    startDate: 'Start Date',
    endDate: 'End Date',
    
    // Book Appointment
    bookAppointmentHeader: 'Book Appointment',
    bookAppointmentDescription: 'Select a date and available time slot to book your gym session.',
    bookingPeriodRestriction: 'You can only book appointments within your subscription period.',
    spotsAvailable: 'spots available',
    spotAvailable: 'spot available',
    ofSpotsAvailable: 'of',
    notAvailableForMorningClients: 'Not available for morning clients',
    restrictedForMorningClients: 'Restricted for Morning Clients',
    notAvailableForAfternoonClients: 'Not available for afternoon clients',
    restrictedForAfternoonClients: 'Restricted for Afternoon Clients',
    originally: 'Originally',
    
    // Subscription Status
    waitingForFirstAppointment: 'Waiting for first appointment',
    actualStartDate: 'Actual Start Date',
    actualEndDate: 'Actual End Date',
    frozenAt: 'Frozen At',
    remainingDaysAtFreeze: 'Remaining Days at Freeze',
    frozen: 'Frozen',
    activated: 'Activated',
    waiting: 'Waiting',
    sessions: 'sessions',
    
    // Common error messages
    bookingNotAllowed: 'Booking Not Allowed',
    onlyOneAppointmentPerDay: 'Only one appointment per day is allowed',
    monthlyLimitReached: 'You have reached your monthly appointment limit.',
    appointmentAlreadyExists: 'An appointment already exists for this time slot',
    appointmentBookingFailed: 'Failed to book appointment',
    appointmentCancelledSuccessfully: 'Appointment cancelled successfully!',
    appointmentCancelledFor: 'Appointment cancelled for {date} at {time}',
    appointmentBookedSuccessfully: 'Appointment booked successfully!',
    noAppointments: 'No appointments',
    
    // Greek month names
    monthNames: ['Ιαν', 'Φεβ', 'Μαρ', 'Απρ', 'Μαϊ', 'Ιουν', 'Ιουλ', 'Αυγ', 'Σεπ', 'Οκτ', 'Νοε', 'Δεκ'],
    dayNames: ['Κυριακή', 'Δευτέρα', 'Τρίτη', 'Τετάρτη', 'Πέμπτη', 'Παρασκευή', 'Σάββατο'],
    timeslotClients: 'Timeslot Clients',
    noClientsBooked: 'No clients booked for this timeslot',
    expressedInterests: 'Expressed Interests',
    availableSlots: 'Available Slots',
    overrides: 'Overrides',
    removeOverride: 'Remove Override',
    confirmRemoveOverride: 'Are you sure you want to remove this override?',
    confirmDisableTimeslot: 'Are you sure you want to disable this timeslot?',
    changing: 'Changing...',
    closing: 'Closing...',
    
    // Global Timeslots
    globalTimeslotManagement: 'Global Timeslot Management',
    filterByDay: 'Filter by Day',
    allDays: 'All Days',
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday',
    createTimeslot: 'Create Timeslot',
    createNewTimeslot: 'Create New Timeslot',
    dayOfWeek: 'Day of Week',
    startTime: 'Start Time',
    endTime: 'End Time',
    editTime: 'Edit Time',
    enable: 'Enable',
    disable: 'Disable',
    deleteTimeslot: 'Delete',
    resetTimeslot: 'Reset',
    enabled: 'Enabled',
    disabled: 'Disabled',
    originalTime: 'Original Time',
    currentTime: 'Current Time',
    confirmDeleteTimeslot: 'Are you sure you want to delete this timeslot?',
    confirmResetTimeslot: 'Are you sure you want to reset this timeslot to defaults?',
    editingTime: 'Editing Time',
    
    // Modal Actions
    enterClientsEmail: 'Enter client\'s email',
    enterClientsPhone: 'Enter client\'s phone number',
    enterInitialPassword: 'Enter initial password (min 6 characters)',
    activeAccount: 'Active Account',
    errorCreatingClient: 'An error occurred while creating the client',
    createClient: 'Create Client',
    deleteClient: 'Delete Client',
    deleteClientConfirmation: 'Are you sure you want to delete client',
    actionCannotBeUndone: 'This action cannot be undone.',
    clientDeletedSuccessfully: 'Client deleted successfully',
    failedToDeleteClient: 'Failed to delete client',
    errorDeletingClient: 'An error occurred while deleting the client',
    
    // General Modal Actions
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    
    // Language
    language: 'Language',
    changeLanguage: 'Change Language',
    greek: 'Ελληνικά',
    english: 'English',
    
    // Client Profile
    viewYourAccountInformation: 'View your account information and manage your appointments',
    personalInformation: 'Personal Information',
    readOnly: '',
    memberSince: 'Member Since',
    eligibilityWindow: 'Eligibility Window',
    activationStatus: 'Activation Status',
    expired: 'Expired',
    inactive: 'Inactive',
    active: 'Active',
    toUpdatePersonalInfo: 'To update your personal information, please contact the gym administrator.',
    loadingProfile: 'Loading profile...',
    failedToLoadProfile: 'Failed to load profile information',
    
    // Broadcast Email
    broadcastEmailTitle: 'Broadcast Email',
    sendPersonalizedEmails: 'Send personalized emails to your gym clients',
    subject: 'Subject',
    message: 'Message',
    enterEmailSubject: 'Enter email subject',
    enterEmailMessage: 'Enter your message here...',
    activeClientsOnly: 'Send to active clients only',
    characterCount: 'characters',
    insertUserName: 'Insert {UserName}',
    insertUserNameVariable: 'Insert {UserName} variable',
    preview: 'Preview',
    send: 'Send',
    subjectRequired: 'Subject is required',
    subjectTooLong: 'Subject must not exceed 200 characters',
    messageRequired: 'Message is required',
    messageTooLong: 'Message must not exceed 10000 characters',
    fixValidationErrors: 'Please fix the validation errors below',
    failedToSendEmail: 'Failed to send broadcast email',
    errorSendingEmail: 'An error occurred while sending the broadcast email. Please try again.',
    attachments: 'Attachments',
    uploadingAttachment: 'Uploading...',
    attachmentsTooLarge: 'Combined attachment size must not exceed 10MB',
    attachmentUploadFailed: 'Failed to upload attachment',
    attachmentsSizeNote: 'Attachments must total no more than 10MB combined.',

    // Edit Client Modals
    editClient: 'Edit Client',
    editClientInfo: 'Edit Client Information',
    editSubscription: 'Edit Subscription',
    editAppointments: 'Edit Appointments',
    clientType: 'Client Type',
    regularClient: 'Regular Client',
    morningClient: 'Morning Client',
    afternoonClient: 'Afternoon Client',
    cardiologyProof: 'Cardiology Proof',
    gender: 'Gender',
    male: 'Male',
    female: 'Female',
    genderNotSet: 'Not set - choose Male or Female',
    isActive: 'Is Active',
    subscriptionDetails: 'Subscription Details',
    doesNotExpire: 'Does not expire',
    monthlyLimitInfo: 'Monthly appointment limit (0 = unlimited)',
    appointmentLimitReached: 'Monthly appointment limit reached',
    cannotBookMoreAppointments: 'Cannot book more appointments this month',
    
    // Confirmation Dialog - these are dynamic but adding defaults
    areYouSure: 'Are you sure?',
    thisActionCannotBeUndone: 'This action cannot be undone.',
    
    // Additional Client Profile keys
    validAppointments: 'Valid Appointments',
    validAppointmentCount: 'Valid Appointment Count',
    attendedAppointmentCount: 'Attended Workouts',
    appointmentsRemaining: 'Appointments Remaining',
    daysRemaining: 'Days Remaining',
    days: 'days',
    subscriptionExpiresIn: 'Your subscription expires in',
    appointmentsRunningLow: 'Your appointments are running low',
    appointmentsLeft: 'left',
    pleaseContactGym: '. Please contact the gym to renew.',
    noActiveSubscription: 'No active subscription found. Please contact the gym administrator to set up your membership.',
    
    // Additional Broadcast Email keys
    sendToActiveClientsOnly: 'Send to active clients only',
    sendToActiveClientsOnlyDescription: 'When checked, emails will only be sent to clients with active accounts. Uncheck to include inactive clients as well.',
    clearForm: 'Clear Form',
    sendingEmail: 'Sending Email...',
    sendBroadcastEmail: 'Send Broadcast Email',
    noSubjectEntered: 'No subject entered',
    noMessageEntered: 'No message entered',
    audience: 'Audience',
    allClients: 'All clients (including inactive)',
    scheduleBroadcastQuestion: 'Schedule this email?',
    sendNow: 'Send now',
    scheduleForLater: 'Schedule for later',
    scheduledSendTime: 'Scheduled send time',
    scheduledSendTimeHelper: '',
    scheduledSendTimeRequired: 'Scheduled send time is required',
    scheduledSendTimeInvalid: 'Scheduled send time is invalid',
    scheduledSendTimeFuture: 'Scheduled send time must be in the future',
    schedule: 'Schedule',
    scheduledFor: 'Scheduled for',
    sendImmediately: 'Send immediately',
    selectedActiveClientsOnly: 'Current selection: active clients only',
    selectedAllClients: 'Current selection: all clients',
    selectedSendNow: 'Current selection: send now',
    selectedScheduleForLater: 'Current selection: schedule for later',
    
    // Timeslot Management - Change Capacity Modal
    changeCapacity: 'Change Capacity',
    changeCapacityFor: 'Change Capacity for',
    newCapacity: 'New Capacity',
    updating: 'Updating...',
    
    // Broadcast Email - Additional
    enterMessagePlaceholder: 'Enter your message here. Use {UserName} to personalize with each client\'s name.',
    specificClients: 'Specific clients',
    specificClientsDescription: 'Send only to selected clients',
    searchClientsPlaceholder: 'Search by name or email...',
    loadingClients: 'Loading clients...',
    clientsSelected: 'client(s) selected',
    noClientsSelectedError: 'Please select at least one client',
    audienceSpecificClients: 'Current selection: specific clients',
    selectAll: 'Select all',
    clearSelection: 'Clear selection',
    characters: 'characters',
    note: 'Note',
    availableVariables: 'Available Variables',
    variableWillBeReplaced: 'Will be replaced with each client\'s actual name',
    variableExample: 'Example',
    variableExampleText: 'becomes',
    lineBreaksNote: 'Line breaks (\\n) will be converted to HTML line breaks (&lt;br&gt;) in the email.',
    
    // Body Fat Measurements
    bodyFatMeasurements: 'Body Fat Measurements',
    latestMeasurement: 'Latest Notification',
    measurementHistory: 'Notification History',
    measurementDate: 'Notification Date',
    lastMeasurementDate: 'Previous Notification Date',
    addMeasurement: 'Add Notification',
    editMeasurement: 'Edit Notification',
    deleteMeasurement: 'Delete Notification',
    confirmDeleteMeasurement: 'Are you sure you want to delete this notification?',
    noMeasurements: 'No notifications found.',
    measurementCreatedSuccessfully: 'Notification created successfully',
    measurementUpdatedSuccessfully: 'Notification updated successfully',
    measurementDeletedSuccessfully: 'Notification deleted successfully',
    failedToLoadMeasurements: 'Failed to load notifications',
    failedToCreateMeasurement: 'Failed to create notification',
    failedToUpdateMeasurement: 'Failed to update notification',
    failedToDeleteMeasurement: 'Failed to delete notification',
    noLastMeasurement: 'N/A',
    showHistory: 'Show History',
    hideHistory: 'Hide History',
    measurementsSection: 'Body Fat Measurements'
  },
  
  el: {
    // Common
    loading: 'Φόρτωση...',
    error: 'Σφάλμα',
    success: 'Επιτυχία',
    save: 'Αποθήκευση',
    cancel: 'Ακύρωση',
    edit: 'Επεξεργασία',
    delete: 'Διαγραφή',
    add: 'Προσθήκη',
    create: 'Δημιουργία',
    update: 'Ενημέρωση',
    search: 'Αναζήτηση',
    confirm: 'Επιβεβαίωση',
    back: 'Πίσω',
    next: 'Επόμενο',
    previous: 'Προηγούμενο',
    close: 'Κλείσιμο',
    submit: 'Υποβολή',
    reset: 'Επαναφορά',
    clear: 'Καθαρισμός',
    required: 'Απαιτείται',
    optional: 'Προαιρετικό',
    yes: 'Ναι',
    no: 'Όχι',
    
    // Edit Subscription Modal
    subscriptionUpdatedSuccessfully: 'Η συνδρομή ενημερώθηκε με επιτυχία',
    failedToUpdateSubscription: 'Αποτυχία ενημέρωσης συνδρομής',
    errorUpdatingSubscription: 'Σφάλμα κατά την ενημέρωση συνδρομής',
    subscriptionFrozenSuccessfully: 'Η συνδρομή παγώθηκε με επιτυχία',
    failedToFreezeSubscription: 'Αποτυχία παγώματος συνδρομής',
    errorFreezingSubscription: 'Σφάλμα κατά το πάγωμα συνδρομής',
    subscriptionUnfrozenSuccessfully: 'Η συνδρομή ξεπάγωσε με επιτυχία',
    failedToUnfreezeSubscription: 'Αποτυχία ξεπαγώματος συνδρομής',
    errorUnfreezingSubscription: 'Σφάλμα κατά το ξεπάγωμα συνδρομής',
    currentSubscription: 'Τρέχουσα Συνδρομή',
    unfreeze: 'Ξεπάγωμα',
    freeze: 'Πάγωμα',
    unfreezeSubscription: 'Ξεπάγωμα αυτής της συνδρομής',
    freezeSubscription: 'Πάγωμα αυτής της συνδρομής',
    appointmentsPerMonth: 'συνεδρίες ανά μήνα',
    clientHasNoSubscription: 'Αυτός ο πελάτης δεν έχει συνδρομή αυτή τη στιγμή.',
    updateSubscription: 'Ενημέρωση Συνδρομής',
    createNewSubscription: 'Δημιουργία Νέας Συνδρομής',
    updateCurrentSubscription: 'Ενημέρωση Τρέχουσας Συνδρομής',
    createNewSubscriptionDescription: 'Δημιουργήστε μια εντελώς νέα συνδρομή για αυτόν τον πελάτη (θα αντικαταστήσει την υπάρχουσα συνδρομή)',
    updateSubscriptionDescription: 'Τροποποιήστε τις λεπτομέρειες της τρέχουσας συνδρομής συμπεριλαμβανομένων των πραγματικών ημερομηνιών',
    subscriptionCreatedSuccessfully: 'Η συνδρομή δημιουργήθηκε επιτυχώς',
    failedToCreateSubscription: 'Απέτυχε η δημιουργία της συνδρομής',
    errorCreatingSubscription: 'Σφάλμα στη δημιουργία συνδρομής',
    creating: 'Δημιουργία...',
    activeSubscription: 'Ενεργή Συνδρομή',
    createSubscription: 'Δημιουργία Συνδρομής',
    
    // Edit Appointments Modal
    manageAppointments: 'Διαχείριση Ραντεβού',
    failedToLoadClientAppointments: 'Αποτυχία φόρτωσης ραντεβού πελάτη',
    failedToLoadAvailableTimeslots: 'Αποτυχία φόρτωσης διαθέσιμων ωρών',
    confirmBookAppointmentMessage: 'Θέλετε να κλείσετε ραντεβού για τον/την',
    failedToBookAppointment: 'Αποτυχία κλεισίματος ραντεβού',
    errorBookingAppointment: 'Σφάλμα κατά το κλείσιμο ραντεβού',
    cancelAppointment: 'Ακύρωση Ραντεβού',
    confirmCancelAppointmentMessage: 'Είστε σίγουροι ότι θέλετε να ακυρώσετε αυτό το ραντεβού; Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.',
    cancelCountsTowardLimit: 'Η ακύρωση να υπολογιστεί στο μηνιαίο όριο ραντεβού του πελάτη',
    cancellationDeadlineWarning: 'Το χρονικό όριο για δωρεάν ακύρωση της προπόνησης έχει παρέλθει. Σε περίπτωση που προχωρήσετε, η θέση σας θα απελευθερωθεί και η προπόνηση θα χρεωθεί κανονικά στο πακέτο σας. Επιθυμείτε να συνεχίσετε;',
    failedToCancelAppointment: 'Αποτυχία ακύρωσης ραντεβού',
    errorCancellingAppointment: 'Σφάλμα κατά την ακύρωση ραντεβού',
    deleteAppointment: 'Διαγραφή Ραντεβού',
    confirmPermanentDeleteMessage: 'Είστε σίγουροι ότι θέλετε να διαγράψετε οριστικά αυτό το ραντεβού; Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.',
    appointmentDeletedSuccessfully: 'Το ραντεβού διαγράφηκε επιτυχώς',
    failedToDeleteAppointment: 'Αποτυχία διαγραφής ραντεβού',
    errorDeletingAppointment: 'Σφάλμα κατά τη διαγραφή ραντεβού',
    loadingAvailableTimeSlots: 'Φόρτωση διαθέσιμων ωρών...',
    noAvailableTimeSlots: 'Δεν Υπάρχουν Διαθέσιμες Ώρες',
    noAvailableTimeSlotsMessage: 'Δεν υπάρχουν διαθέσιμες ώρες για αυτή την ημερομηνία. Παρακαλώ επιλέξτε διαφορετική ημερομηνία.',
    noAppointmentsFound: 'Δεν βρέθηκαν ραντεβού.',
    at: 'στις',
    by: 'από',
    of: 'από',
    
    // Edit Client Info Modal
    clientInfoUpdatedSuccessfully: 'Οι πληροφορίες πελάτη ενημερώθηκαν με επιτυχία',
    failedToUpdateClient: 'Αποτυχία ενημέρωσης πελάτη',
    errorUpdatingClient: 'Σφάλμα κατά την ενημέρωση πελάτη',
    receiveMailNotifications: 'Λήψη email ειδοποιήσεων για πελάτη',
    updateClientInfo: 'Ενημέρωση Πληροφοριών Πελάτη',
    
    // Pagination
    itemsPerPage: 'Στοιχεία ανά σελίδα',
    showingItemsRange: 'Εμφάνιση {start}-{end} από {total} στοιχεία',
    
    // Navigation & Layout
    welcome: 'Καλώς ήρθες',
    dashboard: 'Πίνακας Ελέγχου',
    logout: 'Αποσύνδεση',
    administrator: 'Διαχειριστής',
    client: 'Ασκούμενος',
    
    // Auth
    login: 'Σύνδεση',
    signIn: 'Είσοδος',
    signingIn: 'Σύνδεση...',
    pleaseSignInToContinue: 'Παρακαλώ συνδεθείτε για να συνεχίσετε',
    email: 'Email',
    password: 'Κωδικός Πρόσβασης',
    newPassword: 'Νέος Κωδικός Πρόσβασης',
    confirmPassword: 'Επιβεβαίωση Κωδικού',
    confirmNewPassword: 'Επιβεβαίωση Νέου Κωδικού',
    fullName: 'Ονοματεπώνυμο',
    phoneNumber: 'Τηλέφωνο',
    createAccount: 'Δημιουργία Λογαριασμού',
    creatingAccount: 'Δημιουργία Λογαριασμού...',
    joinReforged: 'Εγγραφή στο Reforged Training Zone',
    backToLogin: 'Επιστροφή στη Σύνδεση',
    forgotPassword: 'Ξεχάσατε τον Κωδικό;',
    resetPassword: 'Επαναφορά Κωδικού',
    sendResetInstructions: 'Αποστολή Οδηγιών Επαναφοράς',
    sending: 'Αποστολή...',
    enterEmailToReceiveReset: 'Εισάγετε το email σας για να λάβετε οδηγίες επαναφοράς',
    emailAddress: 'Διεύθυνση Email',
    securityVerification: 'Επαλήθευση Ασφαλείας',
    
    // Dashboard Navigation
    overview: 'Επισκόπηση',
    clientManagement: 'Διαχείριση Πελατών',
    settings: 'Ρυθμίσεις',
    appointment: 'Ραντεβού',
    bookAppointment: 'Κλείσιμο Ραντεβού',
    myProfile: 'Το Προφίλ μου',
    
    // Admin Overview
    upcomingAppointments: 'Επερχόμενα Ραντεβού',
    activeSubscriptions: 'Ενεργές Συνδρομές',
    inactiveClients: 'Ανενεργοί Πελάτες',
    totalClients: 'Σύνολο Πελατών',
    statistics: 'Στατιστικά',
    clientTotals: 'Σύνολα Πελατών',
    clientDemographics: 'Δημογραφικά Πελατών',
    maleClients: 'Άνδρες Πελάτες',
    femaleClients: 'Γυναίκες Πελάτες',
    genderNotSpecified: 'Δεν Έχει Οριστεί',
    morningClients: 'Πρωινοί Πελάτες',
    afternoonClients: 'Απογευματινοί Πελάτες',
    regularClients: 'Κανονικοί Πελάτες',
    noUpcomingAppointments: 'Δεν υπάρχουν επερχόμενα ραντεβού για',
    loadingAppointments: 'Φόρτωση ραντεβού...',
    searchClientsByName: 'Αναζήτηση πελατών με όνομα...',
    noActiveSubscriptions: 'Δεν βρέθηκαν ενεργές συνδρομές',
    noClientsFound: 'Δεν βρέθηκαν πελάτες που να ταιριάζουν με',
    
    // Client Management
    addNewClient: 'Προσθήκη Νέου Πελάτη',
    searchClients: 'Αναζήτηση πελατών...',
    noClientsFoundGeneral: 'Δεν βρέθηκαν πελάτες.',
    name: 'Όνομα',
    subscriptionExpires: 'Λήξη Συνδρομής',
    actions: 'Ενέργειες',
    noSubscription: 'Χωρίς συνδρομή',
    info: 'Πληροφορίες',
    subscription: 'Συνδρομή',
    appointments: 'Συνεδρίες',
    
    // Appointments
    totalAppointments: 'Ολοκληρωθέντα Ραντεβού',
    upcoming: 'ΠΡΟΣΕΧΗ ΡΑΝΤΕΒΟΥ',
    thisMonth: 'Αυτό το Μήνα',
    appointmentHistory: 'Ιστορικό Ραντεβού',
    noUpcomingAppointmentsShort: 'Δεν υπάρχουν επερχόμενα ραντεβού.',
    bookNewAppointment: 'Κλείσιμο Νέου Ραντεβού',
    noAppointmentHistory: 'Δεν υπάρχει διαθέσιμο ιστορικό ραντεβού.',
    showingLastAppointments: 'Προβολή των τελευταίων 5 ραντεβού',
    time: 'Ώρα',
    booked: 'Κλείστηκε',
    cancelledAt: 'Ακυρώθηκε Στις',
    date: 'Ημερομηνία',
    status: 'Κατάσταση',
    
    // Timeslots & Booking
    availableTimeSlots: 'Διαθέσιμες Ώρες',
    noTimeSlots: 'Δεν υπάρχουν Διαθέσιμες Ώρες',
    noTimeSlotsMessage: 'Δεν υπάρχουν διαθέσιμες ώρες για αυτή την ημερομηνία. Παρακαλώ επιλέξτε διαφορετική ημερομηνία.',
    bookThisSlot: 'Κλείσιμο Ραντεβού',
    booking: 'Κλείσιμο...',
    expressInterest: 'Εκδήλωση Ενδιαφέροντος',
    expressing: 'Εκδήλωση...',
    interestExpressed: 'Εκδηλώθηκε Ενδιαφέρον',
    notificationWhenSpotAvailable: 'Θα ειδοποιηθείτε εάν διατεθεί θέση.',
    alreadyExpressedInterest: 'Έχετε ήδη εκδηλώσει ενδιαφέρον για αυτή τη χρονική περίοδο.',
    removeInterest: 'Αφαίρεση Ενδιαφέροντος',
    removing: 'Αφαίρεση...',
    full: 'Πλήρης',
    available: 'Διαθέσιμη',
    capacity: 'Χωρητικότητα',
    loadingTimeslots: 'Φόρτωση διαθέσιμων ωρών...',
    
    // Subscription
    subscriptionPeriod: 'Περίοδος Συνδρομής',
    monthlyLimit: 'Μηνιαίο Σύνολο Προπονήσεων',
    usedThisMonth: 'Χρεωμένες Συνεδρίες',
    appointmentDates: 'Ημερομηνίες Ραντεβού',
    dateOutsideSubscription: 'Ημερομηνία Εκτός Περιόδου Συνδρομής',
    cannotBookOutsideSubscription: 'Δεν μπορείτε να κλείσετε ραντεβού για αυτή την ημερομηνία επειδή είναι εκτός της περιόδου συνδρομής σας.',
    subscriptionValidFrom: 'Η συνδρομή σας ισχύει από',
    to: 'έως',
    selectDateWithinSubscription: 'Παρακαλώ επιλέξτε μια ημερομηνία εντός της περιόδου συνδρομής σας ή επικοινωνήστε με το γυμναστήριο για βοήθεια.',

    // Subscription History
    subscriptionHistory: 'Ιστορικό Συνδρομών',
    viewHistory: 'Προβολή Ιστορικού',
    loadingSubscriptionHistory: 'Φόρτωση ιστορικού συνδρομών...',
    failedToLoadSubscriptionHistory: 'Αποτυχία φόρτωσης ιστορικού συνδρομών',
    errorLoadingSubscriptionHistory: 'Σφάλμα φόρτωσης ιστορικού συνδρομών',
    noSubscriptionHistory: 'Δεν βρέθηκε ιστορικό συνδρομών.',
    actualPeriod: 'Πραγματική Περίοδος',
    createdAt: 'Δημιουργήθηκε',

    // Settings
    adminSettings: 'Ρυθμίσεις Διαχειριστή',
    manageGymConfiguration: 'Διαχείριση ρυθμίσεων γυμναστηρίου και ωραρίων',
    globalSettings: 'Γενικές Ρυθμίσεις',
    globalTimeslots: 'Γενικό Ωράριο',
    broadcastEmail: 'Μαζική Αποστολή Email',
    loadingSettings: 'Φόρτωση ρυθμίσεων...',
    currentValue: 'Τρέχουσα Αξία',
    lastUpdated: 'Τελευταία ενημέρωση',
    
    // Status badges
    confirmed: 'Επιβεβαιωμένο',
    cancelled: 'Ακυρωμένο',
    completed: 'Ολοκληρωμένο',
    pending: 'Εκκρεμές',
    
    // Errors & Messages
    failedToLoadData: 'Αποτυχία φόρτωσης δεδομένων. Παρακαλώ δοκιμάστε ξανά.',
    failedToLoadClients: 'Αποτυχία φόρτωσης πελατών',
    failedToLoadSettings: 'Αποτυχία φόρτωσης ρυθμίσεων',
    registrationSuccessful: 'Η εγγραφή ήταν επιτυχής. Παρακαλώ ελέγξτε το email σας για επιβεβαίωση του λογαριασμού.',
    emailConfirmationRequired: 'Η σύνδεση απέτυχε. Παρακαλώ ελέγξτε τα στοιχεία σας ή επικοινωνήστε με την υποστήριξη αν χρειάζεστε βοήθεια.',
    loginFailedInvalidCredentials: 'Μη έγκυρο email ή κωδικός πρόσβασης. Παρακαλώ ελέγξτε τα στοιχεία σας και δοκιμάστε ξανά.',
    loginFailedEmailNotConfirmed: 'Η διεύθυνση email σας δεν έχει επιβεβαιωθεί. Παρακαλώ ελέγξτε το email σας για σύνδεσμο επιβεβαίωσης.',
    loginFailedUserNotActive: 'Ο λογαριασμός σας έχει απενεργοποιηθεί. Παρακαλώ επικοινωνήστε με τον διαχειριστή του γυμναστηρίου για βοήθεια.',
    loginFailedNoActiveSubscription: 'Δεν βρέθηκε ενεργή συνδρομή. Παρακαλώ επικοινωνήστε με τον διαχειριστή για την ενεργοποίηση της συνδρομής σας.',
    loginFailedGeneric: 'Παρουσιάστηκε σφάλμα κατά τη σύνδεση. Παρακαλώ δοκιμάστε ξανά.',
    passwordResetSent: 'Αν η διεύθυνση email υπάρχει στο σύστημά μας, θα λάβετε οδηγίες επαναφοράς κωδικού.',
    
    // Booking Info
    bookingInformation: 'Πληροφορίες Κλεισίματος Ραντεβού',
    bookingRules: [
      'Μπορείτε να κλείσετε ραντεβού έως 30 ημέρες προκαταβολικά',
      'Τα ραντεβού μπορούν να ακυρωθούν έως 2 ώρες πριν την προγραμματισμένη ώρα',
      'Παρακαλώ φτάστε 5-10 λεπτά πριν την προγραμματισμένη ώρα σας',
      'Δεν μπορείτε να κλείσετε το ίδιο ραντεβού την ίδια ημέρα',
      'Για πλήρεις ώρες, μπορείτε να εκδηλώσετε ενδιαφέρον για ειδοποίηση όταν θα υπάρξει διαθεσιμότητα',
      'Μπορείτε να διαχειριστείτε τα εκδηλωμένα ενδιαφέροντά σας και να τα αφαιρέσετε οποιαδήποτε στιγμή'
    ],
    morningClientRestriction: 'Περιορισμός Πρωινού Πελάτη: Ως πρωινός πελάτης, μπορείτε να κλείσετε ραντεβού μόνο σε ώρες πριν τις 3:00 μ.μ.',
    afternoonClientRestriction: 'Περιορισμός Απογευματινού Πελάτη: Ως απογευματινός πελάτης, μπορείτε να κλείσετε ραντεβού μόνο από τις 3:00 μ.μ. και μετά',
    
    // Reset Password
    resetPasswordTitle: 'Επαναφορά Κωδικού',
    enterYourNewPassword: 'Εισάγετε τον νέο κωδικό σας',
    forEmail: 'για',
    newPasswordRequired: 'Απαιτείται νέος κωδικός πρόσβασης',
    passwordMinLength: 'Ο κωδικός πρόσβασης πρέπει να έχει τουλάχιστον 6 χαρακτήρες',
    passwordMismatch: 'Ο κωδικός και η επιβεβαίωση του κωδικού δεν ταιριάζουν',
    enterNewPasswordPlaceholder: 'Εισάγετε τον νέο κωδικό σας (τουλάχιστον 6 χαρακτήρες)',
    confirmNewPasswordPlaceholder: 'Επιβεβαιώστε τον νέο κωδικό σας',
    resettingPassword: 'Επαναφορά Κωδικού...',
    passwordResetSuccessfully: 'Ο κωδικός επαναφέρθηκε με επιτυχία. Μπορείτε τώρα να συνδεθείτε με τον νέο κωδικό σας.',
    redirectingToLogin: 'Ανακατεύθυνση στη σύνδεση...',
    passwordResetFailed: 'Η επαναφορά κωδικού απέτυχε',
    errorResettingPassword: 'Παρουσιάστηκε σφάλμα κατά την επαναφορά του κωδικού σας',
    invalidResetLink: 'Μη έγκυρος σύνδεσμος επαναφοράς. Παρακαλώ ζητήστε νέα επαναφορά κωδικού.',
    
    // Email Confirmation
    emailConfirmation: 'Επιβεβαίωση Email',
    confirmingYourEmail: 'Επιβεβαίωση του email σας...',
    pleaseWaitWhileWeVerify: 'Παρακαλώ περιμένετε ενώ επαληθεύουμε τη διεύθυνση email σας.',
    emailConfirmedSuccessfully: 'Το Email Επιβεβαιώθηκε με Επιτυχία!',
    yourEmailHasBeenConfirmed: 'Το email σας έχει επιβεβαιωθεί με επιτυχία! Μπορείτε τώρα να συνδεθείτε στον λογαριασμό σας.',
    youWillBeRedirected: 'Θα ανακατευθυνθείτε στη σελίδα σύνδεσης σε',
    seconds: 'δευτερόλεπτα',
    goToLoginNow: 'Μετάβαση στη Σύνδεση Τώρα',
    confirmationFailed: 'Η Επιβεβαίωση Απέτυχε',
    returnToLogin: 'Επιστροφή στη Σύνδεση',
    requestNewLink: 'Αίτηση Νέου Συνδέσμου',
    needHelp: 'Χρειάζεστε βοήθεια; Επικοινωνήστε με την ομάδα υποστήριξής μας.',
    invalidConfirmationLink: 'Μη έγκυρος σύνδεσμος επιβεβαίωσης. Παρακαλώ ελέγξτε το email σας και δοκιμάστε ξανά.',
    emailConfirmationFailed: 'Η επιβεβαίωση email απέτυχε. Παρακαλώ δοκιμάστε ξανά ή επικοινωνήστε με την υποστήριξη.',
    invalidOrExpiredLink: 'Μη έγκυρος ή ληγμένος σύνδεσμος επιβεβαίωσης. Παρακαλώ ζητήστε νέο email επιβεβαίωσης.',
    errorDuringConfirmation: 'Παρουσιάστηκε σφάλμα κατά την επιβεβαίωση email. Παρακαλώ δοκιμάστε ξανά ή επικοινωνήστε με την υποστήριξη.',
    emailConfirmedLoginMessage: 'Το email επιβεβαιώθηκε με επιτυχία! Παρακαλώ συνδεθείτε.',
    emailAlreadyConfirmed: 'Το email σας έχει ήδη επιβεβαιωθεί. Μπορείτε τώρα να συνδεθείτε στον λογαριασμό σας.',
    checkEmailForNewLink: 'Παρακαλώ ελέγξτε το email σας για νέο σύνδεσμο επιβεβαίωσης.',
    resendVerificationEmail: 'Αποστολή Email Επιβεβαίωσης',
    verificationEmailResent: 'Το email επιβεβαίωσης στάλθηκε στα εισερχόμενά σας. Παρακαλώ ελέγξτε το email σας.',
    failedToResendEmail: 'Απέτυχε η αποστολή email επιβεβαίωσης. Παρακαλώ δοκιμάστε ξανά.',
    noAccountFoundWithEmail: 'Δεν βρέθηκε λογαριασμός με αυτή τη διεύθυνση email.',
    emailAlreadyVerifiedCanLogin: 'Το email έχει ήδη επιβεβαιωθεί. Μπορείτε να συνδεθείτε.',
    accountDisabledContactSupport: 'Ο λογαριασμός είναι απενεργοποιημένος. Επικοινωνήστε με την υποστήριξη.',
    securityCheckFailedTryAgain: 'Ο έλεγχος ασφαλείας απέτυχε. Παρακαλώ δοκιμάστε ξανά.',
    invalidInputValidation: 'Μη έγκυρα στοιχεία. Παρακαλώ ελέγξτε τα στοιχεία σας.',
    somethingWentWrongTryAgainLater: 'Κάτι πήγε στραβά. Παρακαλώ δοκιμάστε ξανά αργότερα.',
    rateLimitExceeded: 'Παρακαλώ περιμένετε πριν ζητήσετε άλλο email.',
    pleaseCompleteCaptcha: 'Παρακαλώ ολοκληρώστε την επαλήθευση ασφαλείας παρακάτω:',
    
    // Settings & Admin
    gymCapacityPerTimeslot: 'Χωρητικότητα Γυμναστηρίου ανά Ώρα',
    regularClientCancellationDeadline: 'Προθεσμία Ακύρωσης Κανονικού Πελάτη (Ώρες)',
    morningClientCancellationDeadline: 'Προθεσμία Ακύρωσης Πρωινού Πελάτη (Ώρες)',
    maxClientsPerTimeslot: 'Μέγιστος αριθμός πελατών που μπορούν να κλείσουν κάθε ώρα',
    cancellationDeadlineRegular: 'Ώρες πριν το ραντεβού όταν η ακύρωση εξακολουθεί να μετράει για κανονικούς πελάτες',
    cancellationDeadlineMorning: 'Ώρες πριν το ραντεβού όταν η ακύρωση εξακολουθεί να μετράει για πρωινούς πελάτες',
    morningAppointmentCutoff: 'Όριο Πρωινού Ραντεβού',
    morningAppointmentCutoffTime: 'Ώρα που καθορίζει πότε ένα ραντεβού θεωρείται πρωινό',
    morningClientBookingCutoff: 'Ώρα Λήξης Κρατήσεων Πρωινών Πελατών',
    morningClientBookingCutoffDescription: 'Οι πρωινοί πελάτες μπορούν να κλείσουν μόνο ώρες που ξεκινούν πριν από αυτή την ώρα',
    afternoonClientBookingStart: 'Ώρα Έναρξης Κρατήσεων Απογευματινών Πελατών',
    afternoonClientBookingStartDescription: 'Οι απογευματινοί πελάτες μπορούν να κλείσουν μόνο ώρες που ξεκινούν από αυτή την ώρα και μετά',
    failedToUpdateSetting: 'Αποτυχία ενημέρωσης ρύθμισης',
    globalSetting: 'Γενική ρύθμιση',
    saving: 'Αποθήκευση...',
    
    // Timeslot Management
    timeslotManagement: 'Διαχείριση Ωραρίου',
    selectDate: 'Επιλογή Ημερομηνίας',
    previousDay: 'Προηγούμενη Ημέρα',
    nextDay: 'Επόμενη Ημέρα',
    previousMonth: 'Προηγούμενος Μήνας',
    nextMonth: 'Επόμενος Μήνας',
    timeslots: 'Ώρες',
    noTimeslotsForDate: 'Δεν υπάρχουν διαθέσιμες ώρες για αυτή την ημερομηνία',
    failedToLoadTimeslots: 'Αποτυχία φόρτωσης ωρών',
    bookClient: 'Κλείσιμο Πελάτη',
    selectClient: 'Επιλογή Πελάτη',
    selectClientPlaceholder: 'Επιλέξτε πελάτη...',
    reason: 'Αιτιολογία',
    viewClients: 'Προβολή Πελατών',
    changeTime: 'Αλλαγή Ώρας',
    disableTimeslot: 'Απενεργοποίηση Ώρας',
    closeDay: 'Κλείσιμο Ημέρας',
    closeDateRange: 'Κλείσιμο Περιόδου',
    bulkCloseTimeslots: 'Μαζικό Κλείσιμο Ωρών',
    newStartTime: 'Νέα Ώρα Έναρξης',
    newEndTime: 'Νέα Ώρα Λήξης',
    reasonForChange: 'Αιτιολογία αλλαγής',
    reasonForClosure: 'Αιτιολογία κλεισίματος',
    reasonForClosing: 'Αιτιολογία κλεισίματος',
    closeDayPlaceholder: 'π.χ., Αργία, Συντήρηση, κλπ.',
    closeDateRangePlaceholder: 'π.χ., Διακοπές, Ανακαινίσεις, κλπ.',
    selectTimeslotsToClose: 'Επιλέξτε ώρες για κλείσιμο',
    bulkCloseReasonPlaceholder: 'π.χ., Συντήρηση, Εκπαίδευση προσωπικού, κλπ.',
    reviewAndClose: 'Έλεγχος και Κλείσιμο',
    confirmBulkClose: 'Επιβεβαίωση Μαζικού Κλεισίματος',
    bulkCloseConfirmationMessage: 'Πρόκειται να κλείσετε τις επιλεγμένες ώρες για το καθορισμένο εύρος ημερομηνιών. Παρακαλώ ελέγξτε τις λεπτομέρειες παρακάτω:',
    dateRange: 'Εύρος Ημερομηνιών',
    selectedTimeslots: 'Επιλεγμένες Ώρες',
    totalOperations: 'Συνολικές Λειτουργίες',
    confirmAndClose: 'Επιβεβαίωση και Κλείσιμο',
    operationResults: 'Αποτελέσματα Λειτουργίας',
    successful: 'Επιτυχής',
    failed: 'Αποτυχία',
    errors: 'Σφάλματα',
    pleaseSelectAtLeastOneTimeslot: 'Παρακαλώ επιλέξτε τουλάχιστον μία ώρα',
    bulkOperationCompletedSuccessfully: 'Η μαζική λειτουργία ολοκληρώθηκε επιτυχώς',
    bulkOperationPartialSuccess: 'Η μαζική λειτουργία ολοκληρώθηκε μερικώς',
    bulkOperationFailed: 'Η μαζική λειτουργία απέτυχε',
    startDate: 'Ημερομηνία Έναρξης',
    endDate: 'Ημερομηνία Λήξης',
    
    // Book Appointment
    bookAppointmentHeader: 'Κλείσιμο Ραντεβού',
    bookAppointmentDescription: 'Επιλέξτε ημερομηνία και διαθέσιμη ώρα για να κλείσετε το ραντεβού σας στο γυμναστήριο.',
    bookingPeriodRestriction: 'Μπορείτε να κλείσετε ραντεβού μόνο εντός της περιόδου συνδρομής σας.',
    spotsAvailable: 'διαθέσιμες θέσεις',
    spotAvailable: 'διαθέσιμη θέση',
    ofSpotsAvailable: 'από',
    notAvailableForMorningClients: 'Μη διαθέσιμο για πρωινούς πελάτες',
    restrictedForMorningClients: 'Περιορισμένο για Πρωινούς Πελάτες',
    notAvailableForAfternoonClients: 'Μη διαθέσιμο για απογευματινούς πελάτες',
    restrictedForAfternoonClients: 'Περιορισμένο για Απογευματινούς Πελάτες',
    originally: 'Αρχικά',
    
    // Subscription Status
    waitingForFirstAppointment: 'Αναμονή πρώτου ραντεβού',
    actualStartDate: 'Πραγματική Ημερομηνία Έναρξης',
    actualEndDate: 'Πραγματική Ημερομηνία Λήξης',
    frozenAt: 'Παγώθηκε στις',
    remainingDaysAtFreeze: 'Ημέρες που Απομένουν στην Παγωμένη Κατάσταση',
    frozen: 'Παγωμένη',
    activated: 'Ενεργοποιημένη',
    waiting: 'Αναμονή',
    sessions: 'συνεδρίες',
    
    // Common error messages
    bookingNotAllowed: 'Δεν Επιτρέπεται το Κλείσιμο',
    onlyOneAppointmentPerDay: 'Επιτρέπεται μόνο ένα ραντεβού ανά ημέρα',
    monthlyLimitReached: 'Έχετε φτάσει το μηνιαίο όριο των συνεδριών σας.',
    appointmentAlreadyExists: 'Υπάρχει ήδη ραντεβού για αυτή την ώρα',
    appointmentBookingFailed: 'Απέτυχε το κλείσιμο ραντεβού',
    appointmentCancelledSuccessfully: 'Το ραντεβού ακυρώθηκε επιτυχώς!',
    appointmentCancelledFor: 'Το ραντεβού ακυρώθηκε για τις {date} στις {time}',
    appointmentBookedSuccessfully: 'Το ραντεβού κλείστηκε επιτυχώς!',
    noAppointments: 'Δεν υπάρχουν ραντεβού',
    
    // Greek month names
    monthNames: ['Ιαν', 'Φεβ', 'Μαρ', 'Απρ', 'Μαϊ', 'Ιουν', 'Ιουλ', 'Αυγ', 'Σεπ', 'Οκτ', 'Νοε', 'Δεκ'],
    dayNames: ['Κυριακή', 'Δευτέρα', 'Τρίτη', 'Τετάρτη', 'Πέμπτη', 'Παρασκευή', 'Σάββατο'],
    timeslotClients: 'Πελάτες Ώρας',
    noClientsBooked: 'Δεν έχουν κλειστεί πελάτες για αυτή την ώρα',
    expressedInterests: 'Εκδηλωμένα Ενδιαφέροντα',
    availableSlots: 'Διαθέσιμες Θέσεις',
    overrides: 'Παρακάμψεις',
    removeOverride: 'Αφαίρεση Παράκαμψης',
    confirmRemoveOverride: 'Είστε σίγουροι ότι θέλετε να αφαιρέσετε αυτή την παράκαμψη;',
    confirmDisableTimeslot: 'Είστε σίγουροι ότι θέλετε να απενεργοποιήσετε αυτή την ώρα;',
    changing: 'Αλλαγή...',
    closing: 'Κλείσιμο...',
    
    // Global Timeslots
    globalTimeslotManagement: 'Διαχείριση Γενικού Ωραρίου',
    filterByDay: 'Φίλτρο ανά Ημέρα',
    allDays: 'Όλες οι Ημέρες',
    monday: 'Δευτέρα',
    tuesday: 'Τρίτη',
    wednesday: 'Τετάρτη',
    thursday: 'Πέμπτη',
    friday: 'Παρασκευή',
    saturday: 'Σάββατο',
    sunday: 'Κυριακή',
    createTimeslot: 'Δημιουργία Ώρας',
    createNewTimeslot: 'Δημιουργία Νέας Ώρας',
    dayOfWeek: 'Ημέρα της Εβδομάδας',
    startTime: 'Ώρα Έναρξης',
    endTime: 'Ώρα Λήξης',
    editTime: 'Επεξεργασία Ώρας',
    enable: 'Ενεργοποίηση',
    disable: 'Απενεργοποίηση',
    deleteTimeslot: 'Διαγραφή',
    resetTimeslot: 'Επαναφορά',
    enabled: 'Ενεργοποιημένη',
    disabled: 'Απενεργοποιημένη',
    originalTime: 'Αρχική Ώρα',
    currentTime: 'Τρέχουσα Ώρα',
    confirmDeleteTimeslot: 'Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή την ώρα;',
    confirmResetTimeslot: 'Είστε σίγουροι ότι θέλετε να επαναφέρετε αυτή την ώρα στις προεπιλογές;',
    editingTime: 'Επεξεργασία Ώρας',
    
    // Modal Actions
    enterClientsEmail: 'Εισάγετε το email του πελάτη',
    enterClientsPhone: 'Εισάγετε το τηλέφωνο του πελάτη',
    enterInitialPassword: 'Εισάγετε αρχικό κωδικό (τουλάχιστον 6 χαρακτήρες)',
    activeAccount: 'Ενεργός Λογαριασμός',
    errorCreatingClient: 'Παρουσιάστηκε σφάλμα κατά τη δημιουργία του πελάτη',
    createClient: 'Δημιουργία Πελάτη',
    deleteClient: 'Διαγραφή Πελάτη',
    deleteClientConfirmation: 'Είστε σίγουροι ότι θέλετε να διαγράψετε τον πελάτη',
    actionCannotBeUndone: 'Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.',
    clientDeletedSuccessfully: 'Ο πελάτης διαγράφηκε επιτυχώς',
    failedToDeleteClient: 'Αποτυχία διαγραφής πελάτη',
    errorDeletingClient: 'Παρουσιάστηκε σφάλμα κατά τη διαγραφή του πελάτη',
    
    // General Modal Actions
    confirmText: 'Επιβεβαίωση',
    cancelText: 'Ακύρωση',
    
    // Language
    language: 'Γλώσσα',
    changeLanguage: 'Αλλαγή Γλώσσας',
    greek: 'Ελληνικά',
    english: 'English',
    
    // Client Profile
    viewYourAccountInformation: 'Δείτε τις πληροφορίες του λογαριασμού σας',
    personalInformation: 'Προσωπικές Πληροφορίες',
    readOnly: '',
    memberSince: 'Μέλος από',
    eligibilityWindow: 'Περίοδος Συνδρομής',
    activationStatus: 'Κατάσταση Συνδρομής',
    expired: 'Ληγμένη',
    inactive: 'Ανενεργή',
    active: 'Ενεργή',
    toUpdatePersonalInfo: 'Για να ενημερώσετε τις προσωπικές σας πληροφορίες, επικοινωνήστε με τον διαχειριστή του γυμναστηρίου.',
    loadingProfile: 'Φόρτωση προφίλ...',
    failedToLoadProfile: 'Αποτυχία φόρτωσης πληροφοριών προφίλ',
    
    // Broadcast Email
    broadcastEmailTitle: 'Μαζική Αποστολή Email',
    sendPersonalizedEmails: 'Στείλτε προσωποποιημένα email στους πελάτες του γυμναστηρίου σας',
    subject: 'Θέμα',
    message: 'Μήνυμα',
    enterEmailSubject: 'Εισάγετε θέμα email',
    enterEmailMessage: 'Εισάγετε το μήνυμά σας εδώ...',
    activeClientsOnly: 'Αποστολή μόνο σε ενεργούς πελάτες',
    characterCount: 'χαρακτήρες',
    insertUserName: 'Εισαγωγή {UserName}',
    insertUserNameVariable: 'Εισαγωγή μεταβλητής {UserName}',
    preview: 'Προεπισκόπηση',
    send: 'Αποστολή',
    subjectRequired: 'Απαιτείται θέμα',
    subjectTooLong: 'Το θέμα δεν πρέπει να υπερβαίνει τους 200 χαρακτήρες',
    messageRequired: 'Απαιτείται μήνυμα',
    messageTooLong: 'Το μήνυμα δεν πρέπει να υπερβαίνει τους 10000 χαρακτήρες',
    fixValidationErrors: 'Παρακαλώ διορθώστε τα σφάλματα επικύρωσης παρακάτω',
    failedToSendEmail: 'Αποτυχία αποστολής μαζικού email',
    errorSendingEmail: 'Παρουσιάστηκε σφάλμα κατά την αποστολή του μαζικού email. Παρακαλώ δοκιμάστε ξανά.',
    attachments: 'Συνημμένα',
    uploadingAttachment: 'Μεταφόρτωση...',
    attachmentsTooLarge: 'Το συνολικό μέγεθος των συνημμένων δεν πρέπει να υπερβαίνει τα 10MB',
    attachmentUploadFailed: 'Αποτυχία μεταφόρτωσης συνημμένου',
    attachmentsSizeNote: 'Τα συνημμένα δεν πρέπει να υπερβαίνουν συνολικά τα 10MB.',

    // Edit Client Modals
    editClient: 'Επεξεργασία Πελάτη',
    editClientInfo: 'Επεξεργασία Πληροφοριών Πελάτη',
    editSubscription: 'Επεξεργασία Συνδρομής',
    editAppointments: 'Επεξεργασία Ραντεβού',
    clientType: 'Τύπος Πελάτη',
    regularClient: 'Κανονικός Πελάτης',
    morningClient: 'Πρωινός Πελάτης',
    afternoonClient: 'Απογευματινός Πελάτης',
    cardiologyProof: 'Καρδιολογικό Πιστοποιητικό',
    gender: 'Φύλο',
    male: 'Άνδρας',
    female: 'Γυναίκα',
    genderNotSet: 'Δεν έχει οριστεί - επιλέξτε Άνδρας ή Γυναίκα',
    isActive: 'Είναι Ενεργός',
    subscriptionDetails: 'Λεπτομέρειες Συνδρομής',
    doesNotExpire: 'Δεν λήγει',
    monthlyLimitInfo: 'Μηνιαίο όριο ραντεβού (0 = απεριόριστο)',
    appointmentLimitReached: 'Συμπληρώθηκε το μηνιαίο όριο ραντεβού',
    cannotBookMoreAppointments: 'Δεν μπορείτε να κλείσετε περισσότερα ραντεβού αυτό το μήνα',
    
    // Confirmation Dialog - these are dynamic but adding defaults
    areYouSure: 'Είστε σίγουροι;',
    thisActionCannotBeUndone: 'Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.',
    
    // Additional Client Profile keys
    validAppointments: 'Έγκυρες Συνεδρίες',
    validAppointmentCount: 'Αριθμός Έγκυρων Συνεδριών',
    attendedAppointmentCount: 'Προπονήσεις με Παρουσία',
    appointmentsRemaining: 'Συνεδρίες που Απομένουν',
    daysRemaining: 'Ημέρες που Απομένουν',
    days: 'ημέρες',
    subscriptionExpiresIn: 'Η συνδρομή σας λήγει σε',
    appointmentsRunningLow: 'Τα ραντεβού σας τελειώνουν',
    appointmentsLeft: 'απομένουν',
    pleaseContactGym: '. Παρακαλώ επικοινωνήστε με το γυμναστήριο για ανανέωση.',
    noActiveSubscription: 'Δεν βρέθηκε ενεργή συνδρομή. Παρακαλώ επικοινωνήστε με τον διαχειριστή του γυμναστηρίου για να ρυθμίσετε τη συνδρομή σας.',
    
    // Additional Broadcast Email keys
    sendToActiveClientsOnly: 'Αποστολή μόνο σε ενεργούς πελάτες',
    sendToActiveClientsOnlyDescription: 'Όταν επιλεγχθεί, τα μηνύματα θα αποστέλλονται μόνο σε πελάτες με ενεργούς λογαριασμούς. Αν δεν επιλεγχθεί, θα σταλούν επίσης σε ανενεργούς πελάτες.',
    clearForm: 'Καθαρισμός φόρμας',
    sendingEmail: 'Αποστολή email...',
    sendBroadcastEmail: 'Αποστολή μαζικού email',
    noSubjectEntered: 'Δεν εισάγεται θέμα',
    noMessageEntered: 'Δεν εισάγεται μήνυμα',
    audience: 'Προορισμός',
    allClients: 'Όλοι οι πελάτες (συμπεριλαμβανομένων των ανενεργών)',
    scheduleBroadcastQuestion: 'Προγραμματισμός αποστολής;',
    sendNow: 'Αποστολή τώρα',
    scheduleForLater: 'Προγραμματισμός για αργότερα',
    scheduledSendTime: 'Ώρα προγραμματισμένης αποστολής',
    scheduledSendTimeHelper: '',
    scheduledSendTimeRequired: 'Η ώρα προγραμματισμένης αποστολής είναι υποχρεωτική',
    scheduledSendTimeInvalid: 'Η ώρα προγραμματισμένης αποστολής δεν είναι έγκυρη',
    scheduledSendTimeFuture: 'Η ώρα προγραμματισμένης αποστολής πρέπει να είναι στο μέλλον',
    schedule: 'Πρόγραμμα',
    scheduledFor: 'Προγραμματισμένο για',
    sendImmediately: 'Άμεση αποστολή',
    selectedActiveClientsOnly: 'Τρέχουσα επιλογή: μόνο ενεργοί πελάτες',
    selectedAllClients: 'Τρέχουσα επιλογή: όλοι οι πελάτες',
    selectedSendNow: 'Τρέχουσα επιλογή: αποστολή τώρα',
    selectedScheduleForLater: 'Τρέχουσα επιλογή: προγραμματισμός για αργότερα',
    
    // Timeslot Management - Change Capacity Modal
    changeCapacity: 'Αλλαγή Χωρητικότητας',
    changeCapacityFor: 'Αλλαγή Χωρητικότητας για',
    newCapacity: 'Νέα Χωρητικότητα',
    updating: 'Ενημέρωση...',
    
    // Broadcast Email - Additional
    enterMessagePlaceholder: 'Εισάγετε το μήνυμά σας εδώ. Χρησιμοποιήστε {UserName} για προσωποποίηση με το όνομα κάθε πελάτη.',
    specificClients: 'Συγκεκριμένοι πελάτες',
    specificClientsDescription: 'Αποστολή μόνο σε επιλεγμένους πελάτες',
    searchClientsPlaceholder: 'Αναζήτηση με βάση το όνομα ή το email...',
    loadingClients: 'Φόρτωση πελατών...',
    clientsSelected: 'πελάτης/ες επιλεγμένος/οι',
    noClientsSelectedError: 'Παρακαλώ επιλέξτε τουλάχιστον έναν πελάτη',
    audienceSpecificClients: 'Τρέχουσα επιλογή: συγκεκριμένοι πελάτες',
    selectAll: 'Επιλογή όλων',
    clearSelection: 'Εκκαθάριση επιλογής',
    characters: 'χαρακτήρες',
    note: 'Σημείωση',
    availableVariables: 'Διαθέσιμες Μεταβλητές',
    variableWillBeReplaced: 'Θα αντικατασταθεί με το πραγματικό όνομα κάθε πελάτη',
    variableExample: 'Παράδειγμα',
    variableExampleText: 'γίνεται',
    lineBreaksNote: 'Οι αλλαγές γραμμής (\\n) θα μετατραπούν σε HTML αλλαγές γραμμής (&lt;br&gt;) στο email.',
    
    // Body Fat Measurements
    bodyFatMeasurements: 'Λιπομετρήσεις',
    latestMeasurement: 'Τελευταία Ενημέρωση',
    measurementHistory: 'Ιστορικό Ενημερώσεων',
    measurementDate: 'Ημερομηνία Ενημέρωσης',
    lastMeasurementDate: 'Προηγούμενη Ημερομηνία Ενημέρωσης',
    addMeasurement: 'Προσθήκη Ενημέρωσης',
    editMeasurement: 'Επεξεργασία Ενημέρωσης',
    deleteMeasurement: 'Διαγραφή Ενημέρωσης',
    confirmDeleteMeasurement: 'Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή την ενημέρωση;',
    noMeasurements: 'Δεν βρέθηκαν ενημερώσεις.',
    measurementCreatedSuccessfully: 'Η ενημέρωση δημιουργήθηκε επιτυχώς',
    measurementUpdatedSuccessfully: 'Η ενημέρωση ενημερώθηκε επιτυχώς',
    measurementDeletedSuccessfully: 'Η ενημέρωση διαγράφηκε επιτυχώς',
    failedToLoadMeasurements: 'Αποτυχία φόρτωσης ενημερώσεων',
    failedToCreateMeasurement: 'Αποτυχία δημιουργίας ενημέρωσης',
    failedToUpdateMeasurement: 'Αποτυχία ενημέρωσης ενημέρωσης',
    failedToDeleteMeasurement: 'Αποτυχία διαγραφής ενημέρωσης',
    noLastMeasurement: 'Δ/Υ',
    showHistory: 'Εμφάνιση Ιστορικού',
    hideHistory: 'Απόκρυψη Ιστορικού',
    measurementsSection: 'Λιπομετρήσεις'
  }
};

type Language = 'en' | 'el';
type TranslationKey = keyof typeof translations.en;

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => Promise<void>;
  t: (key: TranslationKey | string, fallback?: string) => string;
  formatDate: (dateString: string, options?: { weekday?: boolean; short?: boolean }) => string;
  isLoadingPreference: boolean;
  refreshLanguagePreference: () => Promise<void>;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useTranslation = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('el'); // Default to Greek
  const [isLoadingPreference, setIsLoadingPreference] = useState(true);

  const fetchLanguagePreferenceFromBackend = async () => {
    const authToken = localStorage.getItem('authToken');
    if (authToken) {
      try {
        // Determine if user is admin
        const userData = localStorage.getItem('user');
        let isAdmin = false;
        if (userData) {
          try {
            const parsedUser = JSON.parse(userData);
            isAdmin = parsedUser.role === UserRole.Admin;
          } catch (e) {
            console.error('Error parsing user data:', e);
          }
        }
        
        const response = await apiService.getLanguagePreference(isAdmin);
        if (response.success && response.data) {
          const backendLanguage = response.data as Language;
          if (backendLanguage === 'en' || backendLanguage === 'el') {
            setLanguageState(backendLanguage);
            localStorage.setItem('language', backendLanguage);
          }
        }
      } catch (err) {
      }
    }
  };

  useEffect(() => {
    const initializeLanguage = async () => {
      // Check if user is authenticated
      const authToken = localStorage.getItem('authToken');
      
      if (authToken) {
        // User is logged in - fetch language preference from backend
        await fetchLanguagePreferenceFromBackend();
      } else {
        // User is not logged in - just use localStorage or default
        const savedLanguage = localStorage.getItem('language') as Language;
        if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'el')) {
          setLanguageState(savedLanguage);
        }
      }
      
      setIsLoadingPreference(false);
    };

    initializeLanguage();

    // Listen for user login event
    const handleUserLogin = () => {
      // User logged in - fetch language preference
      fetchLanguagePreferenceFromBackend();
    };

    window.addEventListener('userLoggedIn', handleUserLogin);

    return () => {
      window.removeEventListener('userLoggedIn', handleUserLogin);
    };
  }, []);

  const setLanguage = useCallback(async (newLanguage: Language) => {
    setLanguageState(newLanguage);
    localStorage.setItem('language', newLanguage);

    // Only sync with backend if user is authenticated
    const authToken = localStorage.getItem('authToken');
    if (authToken) {
      try {
        // Determine if user is admin
        const userData = localStorage.getItem('user');
        let isAdmin = false;
        if (userData) {
          try {
            const parsedUser = JSON.parse(userData);
            isAdmin = parsedUser.role === UserRole.Admin;
          } catch (e) {
            console.error('Error parsing user data:', e);
          }
        }

        await apiService.updateLanguagePreference({ languageCode: newLanguage }, isAdmin);
      } catch (err) {
        console.error('Failed to update language preference on backend:', err);
        // Continue anyway - the local change has been applied
      }
    }
  }, []);

  const refreshLanguagePreference = useCallback(async () => {
    await fetchLanguagePreferenceFromBackend();
  }, []);

  const formatDate = useCallback((dateString: string, options?: { weekday?: boolean; short?: boolean }): string => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.getMonth();
    const year = date.getFullYear();
    const weekday = date.getDay();
    
    if (language === 'el') {
      const translationSet = translations[language];
      const monthNames = translationSet.monthNames as string[];
      const dayNames = translationSet.dayNames as string[];
      
      let result = '';
      
      if (options?.weekday) {
        result += dayNames[weekday] + ', ';
      }
      
      result += `${day} ${monthNames[month]} ${year}`;
      
      return result;
    } else {
      // English formatting
      const dateOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: options?.short ? 'short' : 'long',
        day: 'numeric',
      };
      
      if (options?.weekday) {
        dateOptions.weekday = 'long';
      }
      
      return date.toLocaleDateString('en-US', dateOptions);
    }
  }, [language]);

  const t = useCallback((key: TranslationKey | string, fallback?: string): string => {
    const translationSet = translations[language];
    
    // Handle nested keys (e.g., 'bookingRules.0')
    if (key.includes('.')) {
      const parts = key.split('.');
      let value: any = translationSet;
      
      for (const part of parts) {
        if (value && typeof value === 'object') {
          value = value[part];
        } else {
          value = undefined;
          break;
        }
      }
      
      if (value !== undefined) {
        return typeof value === 'string' ? value : String(value);
      }
    }
    
    // Handle direct keys
    const translation = translationSet[key as TranslationKey];
    if (translation !== undefined) {
      return typeof translation === 'string' ? translation : String(translation);
    }
    
    // Fallback to English if not found in current language
    if (language !== 'en') {
      const englishTranslation = translations.en[key as TranslationKey];
      if (englishTranslation !== undefined) {
        return typeof englishTranslation === 'string' ? englishTranslation : String(englishTranslation);
      }
    }
    
    // Return fallback or key itself
    return fallback || key;
  }, [language]);

  const contextValue: LanguageContextType = useMemo(() => ({
    language,
    setLanguage,
    t,
    formatDate,
    isLoadingPreference,
    refreshLanguagePreference,
  }), [language, setLanguage, t, formatDate, isLoadingPreference, refreshLanguagePreference]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageProvider; 